/**
 * Espelho de `backend/src/auth/authz/rank.ts` e `capabilities.ts`. Divergir
 * desta escala/lógica quebra a paridade com o backend — mudou lá, muda aqui
 * também.
 *
 * O `caps` que o backend serve já vem com o teto da empresa aplicado
 * (`effectiveCapsOf` em `auth.service.ts`). Sessão antiga em cache sem `caps`
 * é fail-closed e o `AuthContext` se auto-cura (1 refresh de perfil).
 */

export const RANK = {
  visualizador: 0,
  assistente: 1,
  analista: 2,
  supervisor: 3,
  coordenador: 4,
  gerente: 5,
  diretor: 6,
  presidente: 7,
  desenvolvedor: 8,
  agente: 8,
} as const

export type GlobalTipoCode = keyof typeof RANK

/** Chave livre de capability (vocabulário interno TYPE_SEEDS ou ExternalCapabilities). */
export type CapKey = string

/** true estrito — array/undefined (ex.: allowed_topics) não contam como flag. */
export function capsHas(
  caps: Record<string, unknown> | null | undefined,
  key: CapKey,
): boolean {
  return caps?.[key] === true
}

/** Formato mínimo aceito por effectiveRankOf/effectiveCapsOf. */
export interface AuthzUser {
  rank?: number
  caps?: Record<string, unknown> | null
}

/**
 * Rank efetivo do vínculo. Fail-closed: sem usuário ou sem `rank` → 0
 * (paridade com o `effectiveRankOf` do backend; o tipo de cliente já nasce
 * com rank 0). T12: o fallback por nome de cargo morreu dos dois lados.
 */
export function effectiveRankOf(user: AuthzUser | null | undefined): number {
  return user?.rank ?? 0
}

/**
 * Caps efetivas: `caps` da sessão.
 * Fail-closed: sem nada → `null` (`checkCap` trata `null` como "nenhuma cap").
 */
export function effectiveCapsOf(
  user: AuthzUser | null | undefined,
): Record<string, unknown> | null {
  return user?.caps ?? null
}

export function checkRank(
  user: AuthzUser | null | undefined,
  min: number | GlobalTipoCode,
): boolean {
  if (!user) return false
  const minRank = typeof min === 'number' ? min : RANK[min]
  return effectiveRankOf(user) >= minRank
}

export function checkCap(
  user: AuthzUser | null | undefined,
  key: CapKey | CapKey[],
  mode: 'all' | 'any' = 'all',
): boolean {
  const caps = effectiveCapsOf(user)
  const keys = Array.isArray(key) ? key : [key]
  if (keys.length === 0) return false
  return mode === 'any'
    ? keys.some((k) => capsHas(caps, k))
    : keys.every((k) => capsHas(caps, k))
}
